import { loadCKEditorCloud } from '@ckeditor/ckeditor5-react';

import { ckeditorApi } from '@/services/api/ckeditorService';
import { PutRevision, Revision } from '@/services/types/ckeditor';
import { store } from '@/state/state';

const {
  CKEditor: { Plugin, Editor: _Editor },
  CKEditorPremiumFeatures: { RevisionHistory, RevisionTracker },
} = await loadCKEditorCloud({
  version: '44.3.0',
  premium: true,
  translations: ['en'],
});

type Editor = InstanceType<typeof _Editor>;
type TRevisionHistory = InstanceType<typeof RevisionHistory>;
type TRevisionTracker = InstanceType<typeof RevisionTracker>;
type TRevisionJSON = Parameters<TRevisionHistory['addRevisionData']>[0];
type TRevisionData = Exclude<
  Parameters<TRevisionTracker['saveRevision']>[0],
  undefined
>;

type RevisionHistoryIntegrationConfig = {
  documentId: string;
};

const convertToRevision = (revision: Revision): TRevisionData => {
  return {
    id: revision.revision_id,
    name: revision.name,
    creatorId: revision.creator_id,
    authorsIds: revision.authors_ids || [],
    diffData: revision.diff_data ? JSON.parse(revision.diff_data) : {},
    data: revision.data ? JSON.parse(revision.data) : {},
    attributes: revision.attributes ? JSON.parse(revision.attributes) : {},
    fromVersion: revision.from_version,
    toVersion: revision.to_version,
    isEmptyCurrent: revision.is_empty_current,
  };
};

const convertToRevisionJSON = (revision: Revision): TRevisionJSON => {
  return {
    id: revision.revision_id,
    name: revision.name,
    creatorId: revision.creator_id,
    authorsIds: revision.authors_ids || [],
    diffData: revision.diff_data,
    createdAt: new Date(revision.created_at),
    attributes: revision.attributes ? JSON.parse(revision.attributes) : {},
    fromVersion: revision.from_version,
    toVersion: revision.to_version,
  };
};

const exportToCKEditorRevision = (revision: TRevisionData): PutRevision => {
  if (!revision.id) {
    throw new Error('Revision ID is required');
  }

  return {
    revision: {
      revision_id: revision.id,
      name: revision.name ?? undefined,
      creator_id: revision.creatorId ?? undefined,
      authors_ids: revision.authorsIds ?? undefined,
      from_version: revision.fromVersion ?? undefined,
      to_version: revision.toVersion ?? undefined,
      diff_data: revision.diffData
        ? JSON.stringify(revision.diffData)
        : undefined,
      attributes: revision.attributes
        ? JSON.stringify(revision.attributes)
        : undefined,
      data: revision.data ? JSON.stringify(revision.data) : undefined,
    },
  };
};

class RevisionHistoryIntegration extends Plugin {
  private documentId: string;

  private api: typeof ckeditorApi;

  static get pluginName() {
    return 'RevisionHistoryIntegration';
  }

  static get requires() {
    return ['RevisionHistory'];
  }

  constructor(editor: Editor) {
    super(editor);
    const config = editor.config.get(
      'revisionHistoryIntegration',
    ) as RevisionHistoryIntegrationConfig;
    this.documentId = config.documentId;
    this.api = ckeditorApi;
  }

  async init() {
    const revisionHistory = this.editor.plugins.get('RevisionHistory');

    revisionHistory.adapter = {
      getRevision: async ({ revisionId }) => {
        const result = await store.dispatch(
          this.api.endpoints.getRevision.initiate(
            {
              documentId: this.documentId,
              revisionId,
            },
            { forceRefetch: true },
          ),
        );

        if ('error' in result || !result.data) {
          throw new Error('Failed to fetch revision');
        }

        return convertToRevision(result.data);
      },

      updateRevisions: async (revisionsData) => {
        // Get existing revisions to compare
        const documentData = this.editor.getData();
        const revisionPromises = revisionsData.map((revision) => {
          return this.saveRevision({
            ...revision,
            data: documentData,
          });
        });

        return Promise.all(revisionPromises);
      },
    };

    // Add the revisions data for existing revisions.
    const revisionsData = await this.listRevisions();

    revisionsData.forEach((revisionData) => {
      revisionHistory.addRevisionData(revisionData);
    });
  }

  async saveRevision(revision: TRevisionData): Promise<TRevisionData> {
    if (!revision.id) {
      throw new Error('Revision ID is required');
    }

    const putRevision = exportToCKEditorRevision(revision);
    const result = await store.dispatch(
      this.api.endpoints.updateRevision.initiate({
        documentId: this.documentId,
        data: putRevision,
      }),
    );

    if ('error' in result || !result.data) {
      throw new Error('Failed to save revision');
    }

    return convertToRevision(result.data);
  }

  private async listRevisions(): Promise<TRevisionJSON[]> {
    const result = await store.dispatch(
      this.api.endpoints.listRevisions.initiate(this.documentId, {
        forceRefetch: true,
      }),
    );

    if ('error' in result || !result.data) {
      throw new Error('Failed to fetch revisions');
    }

    return result.data.map(convertToRevisionJSON);
  }
}

export { RevisionHistoryIntegration };
